import {
	type Maybe,
	type SearchConfluenceSpaceType,
	type SearchContainerStatus,
} from '../../../../../common/types';
import { type AggRequest, type AggResponse, fetchAgg } from '../../../../../common/utils/fetch-agg';
import { generateHashFromVariables } from '../../../../../utils/caching/common/key-generator';
import { inMemoryCache } from '../../../../../utils/caching/in-memory';

const operationName = 'SmartsRecommendedContainerQuery';
const SmartsRecommendedContainerQuery = `
query ${operationName}($userId: String!, $tenantId: String!) {
  smarts {
    results: recommendedContainer(
      recommendationsQuery: {
        context: {
          userId: $userId,
          tenantId: $tenantId,
          product: "confluence"
        },
        modelRequestParams: {
          caller: "full-page-search",
          experience: "CgContainerNearbyUser"
        },
        requestingUserId: $userId
      }
    ) {
      id
      score
      container {
        createdDate
        id
        key
        name
        spaceId
        status
        type
        links {
          base
        }
        icon {
          path
        }
      }
    }
  }
}
`;

export type SmartsRecommendedContainer = {
	container: Maybe<{
		createdDate: Maybe<string>;
		icon: Maybe<{
			path: Maybe<string>;
		}>;
		id: string;
		key: Maybe<string>;
		links: Maybe<{
			base: Maybe<string>;
		}>;
		name: Maybe<string>;
		spaceId: string;
		status: Maybe<SearchContainerStatus>;
		type: Maybe<SearchConfluenceSpaceType>;
	}>;
	id: string;
	score: Maybe<number>;
};

export type SmartsRecommendedContainerQueryVariables = {
	tenantId: string;
	userId: string;
};

export type SmartsRecommendedContainerQueryData = {
	smarts: {
		results: Maybe<Array<SmartsRecommendedContainer>>;
	};
};

export const cache: { clear: (key?: string) => void; get: (key: string) => Promise<AggResponse<SmartsRecommendedContainerQueryData>> | undefined; has: (key: string) => boolean; inMemoryDecorator: (key: string, supplier: () => Promise<AggResponse<SmartsRecommendedContainerQueryData>>, expireWhen?: ((result: AggResponse<SmartsRecommendedContainerQueryData>) => boolean) | undefined) => Promise<AggResponse<SmartsRecommendedContainerQueryData>>; set: (key: string, result: Promise<AggResponse<SmartsRecommendedContainerQueryData>>) => void; } = inMemoryCache<AggResponse<SmartsRecommendedContainerQueryData>>();

// Expire the cache if there are errors or edges are empty
const expireWhen = (result: AggResponse<SmartsRecommendedContainerQueryData>) => {
	const { data, errors } = result;
	if (!data || (errors && errors.length > 0)) {
		return true;
	}
	const { results } = data.smarts;
	return !results || results.length === 0;
};

export type SmartsRecommendedContainerQueryProps = Pick<
	AggRequest<SmartsRecommendedContainerQueryVariables>,
	'aggAbsoluteUrl' | 'variables'
>;

export const smartsRecommendedContainerQuery = ({
	variables,
	aggAbsoluteUrl,
}: SmartsRecommendedContainerQueryProps): Promise<AggResponse<SmartsRecommendedContainerQueryData>> =>
	cache.inMemoryDecorator(
		generateHashFromVariables(variables),
		() =>
			fetchAgg<SmartsRecommendedContainerQueryVariables, SmartsRecommendedContainerQueryData>({
				variables,
				aggAbsoluteUrl,
				graphQuery: SmartsRecommendedContainerQuery,
				operationName,
			}),
		expireWhen,
	);
